<template>
  <div class="max-w-3xl mx-auto">
    <h1 class="text-2xl font-semibold text-gray-900">Faire un don</h1>
    
    <div class="mt-8 bg-white p-8 rounded-xl shadow-lg">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label class="block text-lg font-medium text-gray-700 mb-4">
            Je souhaite faire un don de
          </label>
          <div class="relative mt-2 rounded-md shadow-sm">
            <input
              type="number"
              v-model="amount"
              min="5"
              step="1"
              class="block w-full rounded-lg border-gray-300 pl-4 pr-12 py-4 text-xl focus:border-zebu-orange focus:ring-zebu-orange"
              placeholder="Montant"
              :class="{ 'border-red-300': amount < 5 && amount !== '' }"
              :disabled="loading"
            />
            <div class="absolute inset-y-0 right-0 flex items-center pr-4">
              <span class="text-gray-500 text-xl">€</span>
            </div>
          </div>
          <p v-if="amount < 5 && amount !== ''" class="mt-2 text-sm text-red-600">
            Le montant minimum est de 5€
          </p>
        </div>

        <div class="bg-gray-50 rounded-lg p-6 mt-6">
          <p class="text-lg text-gray-700">
            Déductible des impôts à hauteur de 66%. 
            <span v-if="amount >= 5" class="font-medium">
              Soit {{ taxDeduction.toFixed(2) }}€ de déduction.
            </span>
          </p>
        </div>

        <div class="bg-zebu-beige/20 rounded-lg p-6 mt-6">
          <p class="text-gray-700 leading-relaxed">
            Zebunet vous remercie pour votre aide financière qui servira à développer nos actions d'aide au développement dans les différents pays où nous intervenons et à couvrir les frais de fonctionnement de la structure. Votre don donne droit à une réduction fiscale de 66% du montant du don (60% pour les entreprises).
          </p>
        </div>

        <!-- Formulaire pour utilisateur non connecté -->
        <div v-if="!isAuthenticated" class="space-y-6 border-t border-gray-200 pt-6">
          <h2 class="text-lg font-medium text-gray-900">Vos informations</h2>
          
          <!-- Civilité -->
          <div>
            <label class="text-sm font-medium text-gray-700">Civilité</label>
            <div class="mt-2 space-x-4">
              <label class="inline-flex items-center">
                <input
                  type="radio"
                  v-model="form.civility"
                  value="M"
                  class="form-radio text-zebu-orange focus:ring-zebu-orange"
                />
                <span class="ml-2">Monsieur</span>
              </label>
              <label class="inline-flex items-center">
                <input
                  type="radio"
                  v-model="form.civility"
                  value="Mme"
                  class="form-radio text-zebu-orange focus:ring-zebu-orange"
                />
                <span class="ml-2">Madame</span>
              </label>
            </div>
          </div>

          <!-- Nom et Prénom -->
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label for="lastname" class="block text-sm font-medium text-gray-700">Nom</label>
              <input
                type="text"
                id="lastname"
                v-model="form.lastname"
                required
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                :class="{ 'border-red-500': errors.lastname }"
              />
              <p v-if="errors.lastname" class="mt-1 text-sm text-red-600">{{ errors.lastname }}</p>
            </div>
            <div>
              <label for="firstname" class="block text-sm font-medium text-gray-700">Prénom</label>
              <input
                type="text"
                id="firstname"
                v-model="form.firstname"
                required
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                :class="{ 'border-red-500': errors.firstname }"
              />
              <p v-if="errors.firstname" class="mt-1 text-sm text-red-600">{{ errors.firstname }}</p>
            </div>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              id="email"
              v-model="form.email"
              required
              class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              :class="{ 'border-red-500': errors.email }"
            />
            <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
          </div>

          <!-- Société (optionnel) -->
          <div>
            <label for="company" class="block text-sm font-medium text-gray-700">
              Société <span class="text-gray-500">(optionnel)</span>
            </label>
            <input
              type="text"
              id="company"
              v-model="form.company"
              class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
            />
          </div>

          <!-- Adresse -->
          <div>
            <label for="address" class="block text-sm font-medium text-gray-700">Adresse</label>
            <input
              type="text"
              id="address"
              v-model="form.address"
              class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
            />
          </div>

          <!-- Code postal, Ville et Pays -->
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label for="zipcode" class="block text-sm font-medium text-gray-700">Code postal</label>
              <input
                type="text"
                id="zipcode"
                v-model="form.zipcode"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700">Ville</label>
              <input
                type="text"
                id="city"
                v-model="form.city"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>
            <div>
              <label for="country" class="block text-sm font-medium text-gray-700">Pays</label>
              <select
                id="country"
                v-model="form.country"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              >
                <option v-for="country in sortedCountries" :key="country.code" :value="country.code">
                  {{ country.name.trim() }}
                </option>
              </select>
            </div>
          </div>

          <!-- Téléphones -->
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700">
                Téléphone fixe <span class="text-gray-500">(optionnel)</span>
              </label>
              <input
                type="tel"
                id="phone"
                v-model="form.phone"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>
            <div>
              <label for="mobile" class="block text-sm font-medium text-gray-700">
                Téléphone mobile <span class="text-gray-500">(optionnel)</span>
              </label>
              <input
                type="tel"
                id="mobile"
                v-model="form.mobile"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>
          </div>

          <!-- Message (optionnel) -->
          <div>
            <label for="message" class="block text-sm font-medium text-gray-700">
              Message <span class="text-gray-500">(optionnel)</span>
            </label>
            <textarea
              id="message"
              v-model="form.message"
              rows="3"
              class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              placeholder="Votre message..."
            ></textarea>
          </div>

          <!-- Se connecter -->
          <div class="text-center">
            <p class="text-sm text-gray-600">
              Déjà membre ?
              <router-link to="/login" class="font-medium text-zebu-orange hover:text-zebu-orange/80">
                Connectez-vous
              </router-link>
            </p>
          </div>
        </div>

        <div class="mt-8">
          <button
            type="submit"
            class="w-full btn btn-primary py-4 text-lg relative"
            :disabled="!isValidAmount || loading"
          >
            <span v-if="!loading">Valider mon don</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Traitement en cours...
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { memberareaService } from '@/services/memberarea.service';
import { useAuthStore } from '@/stores/auth.store';

const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const amount = ref(5);
const loading = ref(false);
const countries = ref([]);
const errors = ref({});

const form = ref({
  civility: 'M',
  lastname: '',
  firstname: '',
  email: '',
  company: '',
  address: '',
  zipcode: '',
  city: '',
  country: 'FR',
  phone: '',
  mobile: '',
  message: ''
});

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated);

const taxDeduction = computed(() => {
  const numAmount = Number(amount.value);
  return numAmount * 0.66;
});

const isValidAmount = computed(() => {
  const numAmount = Number(amount.value);
  return numAmount >= 5;
});

const sortedCountries = computed(() => {
  // Séparer la France des autres pays
  const france = countries.value.find(country => country.code === 'FR')
  const otherCountries = countries.value
    .filter(country => country.code !== 'FR')
    .sort((a, b) => a.name.localeCompare(b.name, 'fr', { ignorePunctuation: true }))

  return france ? [france, ...otherCountries] : otherCountries
});

// Validate form
const validateForm = () => {
  const newErrors = {};

  if (!isAuthenticated.value) {
    if (!form.value.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!form.value.firstname) {
      newErrors.firstname = 'Le prénom est requis';
    }

    if (!form.value.lastname) {
      newErrors.lastname = 'Le nom est requis';
    }
  }

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

// Load countries on mount
onMounted(async () => {
  try {
    const response = await memberareaService.getCountries();
    countries.value = response;
  } catch (error) {
    console.error('Erreur lors du chargement des pays:', error);
    toast.error("Erreur lors du chargement de la liste des pays");
  }

  // Vérifier si nous sommes sur la page de succès
  const route = router.currentRoute.value;
  console.log('Route actuelle dans DonateView:', route.path);
  console.log('Query params dans DonateView:', route.query);
  
  if (route.path === '/donate/success' && route.query.session_id) {
    console.log('Redirection vers la page de succès détectée, vérification du paiement...');
    await verifyPayment(route.query.session_id);
  }
});

// Gestion de la soumission du formulaire
const handleSubmit = async () => {
  if (!isValidAmount.value || loading.value) return;
  if (!validateForm()) return;

  loading.value = true;
  const donationAmount = Number(amount.value);
  
  console.log('Montant du don à envoyer:', donationAmount);

  try {
    let response;
    
    if (isAuthenticated.value) {
      console.log('Création d\'un don authentifié avec montant:', donationAmount);
      response = await memberareaService.createDonation(donationAmount);
    } else {
      console.log('Création d\'un don public avec montant:', donationAmount);
      response = await memberareaService.createPublicDonation({
        ...form.value,
        amount: donationAmount
      });
    }
    
    console.log('Réponse de création du don:', response);
    
    if (response?.url) {
      // Stocker le montant dans localStorage pour vérification ultérieure
      localStorage.setItem('expectedDonationAmount', donationAmount);
      window.location.href = response.url;
    } else {
      throw new Error('URL de paiement non reçue');
    }
  } catch (error) {
    console.error('Erreur lors du traitement du don:', error);
    toast.error("Une erreur est survenue lors de la création du don. Veuillez réessayer.");
  } finally {
    loading.value = false;
  }
};

// Ajouter cette fonction pour vérifier le statut du paiement
const verifyPayment = async (sessionId) => {
  console.log('Fonction verifyPayment appelée avec sessionId:', sessionId);
  try {
    loading.value = true;
    
    // Vérifier si l'utilisateur est connecté
    const isUserLoggedIn = authStore.isAuthenticated;
    console.log('Utilisateur connecté dans verifyPayment:', isUserLoggedIn);
    
    let response;
    if (isUserLoggedIn) {
      console.log('Appel à verifyDonation depuis verifyPayment');
      response = await memberareaService.verifyDonation(sessionId);
    } else {
      console.log('Appel à verifyPublicDonation depuis verifyPayment');
      response = await memberareaService.verifyPublicDonation(sessionId);
    }
    
    console.log('Réponse de vérification dans verifyPayment:', response);
    
    if (response.success) {
      // Le montant est dans response.donation.amount
      const donationAmount = response.donation?.amount || 0;
      console.log('Montant du don vérifié:', donationAmount);
      
      toast.success(`Merci pour votre don de ${donationAmount}€ !`);
      // Rediriger vers une page de confirmation ou le tableau de bord
      router.push('/memberarea');
    } else {
      console.error('Échec de la vérification:', response);
      toast.error("Une erreur est survenue lors de la vérification du paiement.");
    }
  } catch (error) {
    console.error('Erreur lors de la vérification du paiement:', error);
    console.error('Détails de l\'erreur:', error.response?.data || error.message);
    
    // Vérifier si c'est une erreur de session déjà traitée
    const errorMessage = error.response?.data?.message || error.message || "";
    if (errorMessage.includes("déjà été traitée") || errorMessage.includes("already processed")) {
      // Récupérer le montant attendu du localStorage si disponible
      const expectedAmount = localStorage.getItem('expectedDonationAmount');
      if (expectedAmount) {
        toast.info(`Votre don de ${expectedAmount}€ a déjà été traité.`);
        localStorage.removeItem('expectedDonationAmount');
      } else {
        toast.info("Cette donation a déjà été traitée. Votre don a bien été pris en compte.");
      }
      router.push('/memberarea');
    } else {
      // Pour les autres erreurs, afficher le message d'erreur standard
      toast.error("Une erreur est survenue lors de la vérification du paiement.");
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* Styles de base pour tous les inputs et select */
input[type="text"],
input[type="email"],
input[type="tel"],
input[type="number"],
select,
textarea {
  @apply mt-1 block w-full rounded-lg border-2 border-gray-200 bg-white px-3
  focus:border-zebu-orange focus:ring-1 focus:ring-zebu-orange/20
  hover:border-gray-300
  transition-all duration-200
  shadow-sm;
  min-height: 42px;
}

/* Style spécifique pour le select */
select {
  @apply pr-10 appearance-none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}

/* Style pour les radio buttons */
input[type="radio"] {
  @apply w-5 h-5 border-2 border-gray-300 
  text-zebu-orange 
  focus:ring-2 focus:ring-zebu-orange/30 focus:ring-offset-2
  hover:border-zebu-orange
  transition-colors duration-200
  cursor-pointer;
}

/* Style pour les états disabled */
input:disabled,
select:disabled {
  @apply bg-gray-50 cursor-not-allowed border-gray-200;
}

/* Style pour les états focus */
input:focus,
select:focus,
textarea:focus {
  @apply border-zebu-orange/70 ring-2 ring-zebu-orange/20 outline-none;
}

/* Style pour les états hover */
input:hover:not(:disabled),
select:hover:not(:disabled),
textarea:hover:not(:disabled) {
  @apply border-gray-300;
}
</style> 